import React, { useState, useEffect, useCallback, memo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Grid,
  Paper,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Divider,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Slider,
  Tooltip,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  Image as ImageIcon,
  ColorLens as ColorLensIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  ContentCopy as ContentCopyIcon,
  FormatColorFill as FormatColorFillIcon,
  Palette as PaletteIcon,
  Photo as PhotoIcon,
  TextFields as TextFieldsIcon,
  ViewQuilt as ViewQuiltIcon,
  Info as InfoIcon,
  People as PeopleIcon,
  EventNote as EventNoteIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import { axiosInstance } from "../../../../utils/axios";
import { SketchPicker } from "react-color";

// Event landing page preview component
const LandingPagePreview = ({ template, eventDetails }) => {
  const theme = useTheme();

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(parseInt(timestamp));
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    return timeString.substring(0, 5); // Format: HH:MM
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflowY: "auto",
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        boxShadow: theme.shadows[1],
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: 300,
          backgroundImage: template.header.backgroundImage
            ? `url(${template.header.backgroundImage})`
            : "none",
          backgroundColor: template.header.backgroundImage
            ? undefined
            : template.header.backgroundColor,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          textAlign: "center",
          color: template.header.textColor,
          p: 4,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1,
          },
        }}
      >
        <Box sx={{ position: "relative", zIndex: 2 }}>
          <Typography variant="h3" sx={{ mb: 2, fontWeight: "bold" }}>
            {template.header.title || eventDetails?.name || "Your Event Name"}
          </Typography>
          <Typography variant="h6" sx={{ mb: 3 }}>
            {template.header.subtitle ||
              "Join us for an amazing event experience"}
          </Typography>
          {eventDetails && (
            <Typography variant="body1" sx={{ mb: 3 }}>
              {formatDate(eventDetails.startDate)} •{" "}
              {formatTime(eventDetails.startTime)} -{" "}
              {formatTime(eventDetails.endTime)}
            </Typography>
          )}
          {template.header.showButton && (
            <Button
              variant="contained"
              size="large"
              sx={{
                backgroundColor: template.header.buttonColor,
                color: "#fff",
                "&:hover": {
                  backgroundColor: alpha(template.header.buttonColor, 0.8),
                },
              }}
            >
              {template.header.buttonText || "Register Now"}
            </Button>
          )}
        </Box>
      </Box>

      {/* About Section */}
      <Box
        sx={{
          py: 6,
          px: 4,
          bgcolor: template.about.backgroundColor || "background.paper",
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            sx={{
              mb: 4,
              textAlign: "center",
              color: template.about.titleColor,
            }}
          >
            {template.about.title || "About The Event"}
          </Typography>
          <Typography
            variant="body1"
            sx={{ whiteSpace: "pre-line", color: template.about.textColor }}
          >
            {template.about.description ||
              eventDetails?.about ||
              "This is where you can add a detailed description of your event. Share what attendees can expect to learn or experience during your event."}
          </Typography>
        </Container>
      </Box>

      {/* Speakers Section */}
      <Box
        sx={{
          py: 6,
          px: 4,
          bgcolor: template.speakers.backgroundColor || "#f5f5f5",
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            sx={{
              mb: 4,
              textAlign: "center",
              color: template.speakers.titleColor,
            }}
          >
            {template.speakers.title || "Speakers"}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mb: 4,
              textAlign: "center",
              color: template.speakers.textColor,
            }}
          >
            {template.speakers.subtitle || "Learn from our expert speakers"}
          </Typography>

          <Grid container spacing={3}>
            {template.speakers.items && template.speakers.items.length > 0 ? (
              template.speakers.items.map((speaker, index) => (
                <Grid item xs={12} sm={6} md={4} key={speaker.id || index}>
                  <Card elevation={2}>
                    <Box
                      sx={{
                        height: 180,
                        bgcolor: "grey.300",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {speaker.image ? (
                        <CardMedia
                          component="img"
                          height="180"
                          image={speaker.image}
                          alt={speaker.name}
                        />
                      ) : (
                        <PhotoIcon fontSize="large" color="disabled" />
                      )}
                    </Box>
                    <CardContent>
                      <Typography variant="h6">{speaker.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {speaker.title}
                        {speaker.organization
                          ? ` at ${speaker.organization}`
                          : ""}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Paper sx={{ p: 3, textAlign: "center" }}>
                  <Typography variant="body1" color="text.secondary">
                    No speakers have been added to this event yet.
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>

      {/* Agenda Section */}
      <Box
        sx={{
          py: 6,
          px: 4,
          bgcolor: template.agenda.backgroundColor || "background.paper",
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            sx={{
              mb: 4,
              textAlign: "center",
              color: template.agenda.titleColor,
            }}
          >
            {template.agenda.title || "Event Schedule"}
          </Typography>

          {template.agenda.items && template.agenda.items.length > 0 ? (
            template.agenda.items.map((session, index) => (
              <Paper
                key={session.id || index}
                elevation={2}
                sx={{
                  mb: 2,
                  p: 3,
                  borderLeft: `5px solid ${
                    template.agenda.accentColor || theme.palette.primary.main
                  }`,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: template.agenda.textColor }}
                >
                  {session.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: alpha(template.agenda.textColor, 0.7), mb: 1 }}
                >
                  {session.time} ({session.duration})
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: template.agenda.textColor }}
                >
                  {session.description}
                </Typography>
              </Paper>
            ))
          ) : (
            <Paper sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary">
                No sessions have been added to this event yet.
              </Typography>
            </Paper>
          )}
        </Container>
      </Box>

      {/* Sponsors Section */}
      <Box
        sx={{
          py: 6,
          px: 4,
          bgcolor: template.sponsors.backgroundColor || "#f5f5f5",
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            sx={{
              mb: 4,
              textAlign: "center",
              color: template.sponsors.titleColor,
            }}
          >
            {template.sponsors.title || "Our Sponsors"}
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {template.sponsors.items && template.sponsors.items.length > 0 ? (
              template.sponsors.items.map((sponsor, index) => (
                <Grid item xs={6} sm={4} md={3} key={sponsor.id || index}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      height: 120,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    {sponsor.logo ? (
                      <Box
                        component="img"
                        src={sponsor.logo}
                        alt={sponsor.name}
                        sx={{
                          maxWidth: "100%",
                          maxHeight: 70,
                          objectFit: "contain",
                          mb: 1,
                        }}
                      />
                    ) : (
                      <PhotoIcon
                        sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
                      />
                    )}
                    <Typography
                      variant="body2"
                      sx={{ color: template.sponsors.textColor }}
                    >
                      {sponsor.name}
                    </Typography>
                  </Paper>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Paper sx={{ p: 3, textAlign: "center" }}>
                  <Typography variant="body1" color="text.secondary">
                    No sponsors have been added to this event yet.
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>

      {/* Footer Section */}
      <Box
        sx={{
          py: 4,
          px: 4,
          bgcolor: template.footer.backgroundColor || "#212121",
          color: template.footer.textColor || "#ffffff",
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" align="center">
            {template.footer.text ||
              `© ${new Date().getFullYear()} ${
                eventDetails?.organizedBy || "Event Organizer"
              }. All rights reserved.`}
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

// The landing page builder component
const LandingPageBuilder = () => {
  const { id: eventId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  // State for event and template data
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [eventDetails, setEventDetails] = useState(null);
  const [publicUrl, setPublicUrl] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [currentSection, setCurrentSection] = useState("header");
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [colorField, setColorField] = useState(null);
  const [tempColor, setTempColor] = useState(""); // Temporary color for picker
  const [imageUploading, setImageUploading] = useState(false);

  // State for alerts
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Default template structure
  const [template, setTemplate] = useState({
    header: {
      title: "",
      subtitle: "Join us for an amazing event experience",
      backgroundImage: "",
      backgroundColor: "#3f51b5",
      textColor: "#ffffff",
      buttonText: "Register Now",
      buttonColor: "#f50057",
      showButton: true,
    },
    about: {
      title: "About The Event",
      description: "",
      backgroundColor: "#ffffff",
      titleColor: "#212121",
      textColor: "#424242",
    },
    speakers: {
      title: "Speakers",
      subtitle: "Learn from our expert speakers",
      backgroundColor: "#f5f5f5",
      titleColor: "#212121",
      textColor: "#424242",
      items: [],
    },
    agenda: {
      title: "Event Schedule",
      backgroundColor: "#ffffff",
      titleColor: "#212121",
      textColor: "#424242",
      accentColor: "#3f51b5",
      items: [],
    },
    sponsors: {
      title: "Our Sponsors",
      backgroundColor: "#f5f5f5",
      titleColor: "#212121",
      textColor: "#424242",
      items: [],
    },
    footer: {
      text: "",
      backgroundColor: "#212121",
      textColor: "#ffffff",
    },
  });

  // Fallback clipboard function
  const copyToClipboardFallback = () => {
    try {
      // Create temporary input element
      const el = document.createElement("textarea");
      el.value = `${window.location.origin}/events/preview/${publicUrl}`;
      el.setAttribute("readonly", "");
      el.style.position = "absolute";
      el.style.left = "-9999px";
      document.body.appendChild(el);

      // Select and copy
      const selected =
        document.getSelection().rangeCount > 0
          ? document.getSelection().getRangeAt(0)
          : false;
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);

      // Restore selection if any
      if (selected) {
        document.getSelection().removeAllRanges();
        document.getSelection().addRange(selected);
      }

      setAlert({
        open: true,
        message: "URL copied to clipboard",
        severity: "success",
      });
    } catch (err) {
      console.error("Fallback clipboard copy error:", err);
      setAlert({
        open: true,
        message: "Failed to copy URL. Please select and copy manually.",
        severity: "error",
      });
    }
  };

  // Fetch event data
  const fetchEventData = useCallback(async () => {
    try {
      setLoading(true);

      // First try to get the event using the getEvent query which might be more reliable
      const response = await axiosInstance.post("/graphql", {
        query: `
          query GetEvent($eventId: ID!) {
            getEvent(id: $eventId) {
              id
              name
              startDate
              endDate
              startTime
              endTime
              timezone
              about
              organizedBy
              supportEmail
              landingPage {
                sections
                publicUrl
              }
            }
          }
        `,
        variables: {
          eventId,
        },
      });

      let event = null;

      // Check if we got valid data from getEvent
      if (response.data?.getEvent) {
        event = response.data.getEvent;
        console.log("Successfully fetched event data using getEvent:", event);
      } else {
        console.log("getEvent returned null, trying alternative methods...");

        // If getEvent fails, try using event query as a fallback
        const fallbackResponse = await axiosInstance.post("/graphql", {
          query: `
            query FallbackEvent($eventId: ID!) {
              event(id: $eventId) {
                id
                name
                startDate
                endDate
                startTime
                endTime
                timezone
                about
                organizedBy
                supportEmail
                landingPage {
                  sections
                  publicUrl
                }
              }
            }
          `,
          variables: {
            eventId,
          },
        });

        if (fallbackResponse.data?.event) {
          event = fallbackResponse.data.event;
          console.log(
            "Successfully fetched event data using fallback method:",
            event
          );
        }
      }

      if (event) {
        setEventDetails(event);

        // Initialize template with event data
        setTemplate((prevTemplate) => ({
          ...prevTemplate,
          header: {
            ...prevTemplate.header,
            title: event.name || prevTemplate.header.title,
          },
          about: {
            ...prevTemplate.about,
            description: event.about || prevTemplate.about.description,
          },
          footer: {
            ...prevTemplate.footer,
            text: `© ${new Date().getFullYear()} ${
              event.organizedBy || "Event Organizer"
            }. All rights reserved.`,
          },
        }));

        // Load saved landing page if it exists
        if (event.landingPage?.sections) {
          try {
            const sectionsString = event.landingPage.sections;
            if (sectionsString && sectionsString !== "{}") {
              const savedTemplate = JSON.parse(sectionsString);
              console.log("Loaded saved template:", savedTemplate);
              setTemplate(savedTemplate);
            }
          } catch (e) {
            console.error("Error parsing landing page data:", e);
          }
        }

        // Set public URL
        if (event.landingPage?.publicUrl) {
          setPublicUrl(event.landingPage.publicUrl);
          setPreviewUrl(`/events/preview/${event.landingPage.publicUrl}`);
        } else {
          generatePublicUrl();
        }

        // Fetch speakers, sessions, and sponsors
        await Promise.all([
          fetchSpeakers(),
          fetchSessions(),
          fetchSponsors(),
          fetchSessionAttendees(),
        ]);
      } else {
        console.error("Could not fetch event data from any endpoint");
        setAlert({
          open: true,
          message:
            "Failed to load event data. Please refresh the page or contact support.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching event data:", error);
      setAlert({
        open: true,
        message:
          "Failed to load event data: " + (error.message || "Unknown error"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  // Fetch speakers
  const fetchSpeakers = async () => {
    try {
      const response = await axiosInstance.post("/graphql", {
        query: `
    query GetEventAttendees($eventId: ID!, $category: String) {
      getEventAttendees(eventId: $eventId, category: $category) {
        id
        firstName
        lastName
        jobTitle
        organization
      }
    }
  `,
        variables: {
          eventId,
          category: "Speaker",
        },
      });

      console.log("Speakers response:", response);

      if (response.data?.getEventAttendees) {
        const speakers = response.data.getEventAttendees.map((speaker) => ({
          id: speaker.id,
          name: `${speaker.firstName} ${speaker.lastName}`,
          title: speaker.jobTitle || "",
          organization: speaker.organization || "",
          image: "",
        }));

        setTemplate((prevTemplate) => {
          // Only update if there's no existing items in the saved template
          if (
            !prevTemplate.speakers.items ||
            prevTemplate.speakers.items.length === 0
          ) {
            return {
              ...prevTemplate,
              speakers: {
                ...prevTemplate.speakers,
                items: speakers,
              },
            };
          }
          return prevTemplate;
        });
      }
    } catch (error) {
      console.error("Error fetching speakers:", error);
    }
  };

  // Fetch sessions
  const fetchSessions = async () => {
    try {
      const response = await axiosInstance.post("/graphql", {
        query: `
          query GetSessions($eventId: ID!) {
            getSessions(eventId: $eventId) {
              id
              name
              sessionDate
              sessionTime
              duration
              description
            }
          }
        `,
        variables: {
          eventId,
        },
      });

      console.log("Sessions response:", response);

      if (response.data?.getSessions) {
        const sessions = response.data.getSessions.map((session) => {
          const sessionDate = new Date(parseInt(session.sessionDate));
          const formattedDate = sessionDate.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
          });

          return {
            id: session.id,
            title: session.name,
            time: `${formattedDate}, ${session.sessionTime.substring(0, 5)}`,
            duration: `${session.duration} minutes`,
            description:
              session.description.length > 100
                ? `${session.description.substring(0, 100)}...`
                : session.description,
          };
        });

        setTemplate((prevTemplate) => {
          // Only update if there's no existing items in the saved template
          if (
            !prevTemplate.agenda.items ||
            prevTemplate.agenda.items.length === 0
          ) {
            return {
              ...prevTemplate,
              agenda: {
                ...prevTemplate.agenda,
                items: sessions,
              },
            };
          }
          return prevTemplate;
        });
      }
    } catch (error) {
      console.error("Error fetching sessions:", error);
    }
  };

  // Fetch sponsors
  const fetchSponsors = async () => {
    try {
      const response = await axiosInstance.post("/graphql", {
        query: `
          query GetBooths($eventId: ID!) {
            getBooths(eventId: $eventId) {
              id
              boothName
              logoUrl
              websiteUrl
            }
          }
        `,
        variables: {
          eventId,
        },
      });

      console.log("Sponsors response:", response);

      if (response.data?.getBooths) {
        const sponsors = response.data.getBooths.map((booth) => ({
          id: booth.id,
          name: booth.boothName,
          logo: booth.logoUrl || "",
          website: booth.websiteUrl || "",
        }));

        setTemplate((prevTemplate) => {
          // Only update if there's no existing items in the saved template
          if (
            !prevTemplate.sponsors.items ||
            prevTemplate.sponsors.items.length === 0
          ) {
            return {
              ...prevTemplate,
              sponsors: {
                ...prevTemplate.sponsors,
                items: sponsors,
              },
            };
          }
          return prevTemplate;
        });
      }
    } catch (error) {
      console.error("Error fetching sponsors:", error);
    }
  };

  // Fetch hosts, co-hosts, and all attendee data for sessions
  const fetchSessionAttendees = async () => {
    try {
      // Fetch hosts
      const hostsResponse = await axiosInstance.post("/graphql", {
        query: `
          query GetHosts($eventId: ID!, $category: String) {
            getEventAttendees(eventId: $eventId, category: $category) {
              id
              firstName
              lastName
              jobTitle
              organization
            }
          }
        `,
        variables: {
          eventId,
          category: "Host",
        },
      });

      // Fetch co-hosts
      const cohostsResponse = await axiosInstance.post("/graphql", {
        query: `
          query GetCoHosts($eventId: ID!, $category: String) {
            getEventAttendees(eventId: $eventId, category: $category) {
              id
              firstName
              lastName
              jobTitle
              organization
            }
          }
        `,
        variables: {
          eventId,
          category: "Co Host",
        },
      });

      console.log("Hosts response:", hostsResponse);
      console.log("Co-hosts response:", cohostsResponse);

      // Format hosts
      const hosts = hostsResponse.data?.getEventAttendees || [];
      const cohosts = cohostsResponse.data?.getEventAttendees || [];

      // Store attendee data in state if needed
      if (hosts.length > 0 || cohosts.length > 0) {
        console.log("Found session hosts/co-hosts:", { hosts, cohosts });
        // Here you can store or use this data as needed
        // For example, if you need to include host information in session items
      }
    } catch (error) {
      console.error("Error fetching session attendees:", error);
    }
  };

  // Generate a unique public URL
  const generatePublicUrl = () => {
    const randomStr = Math.random().toString(36).substring(2, 8);
    const url = `event-${eventId}-${randomStr}`;
    setPublicUrl(url);
    setPreviewUrl(`/events/preview/${url}`);
  };

  // Handle template changes - Memoized to prevent re-renders
  const handleTemplateChange = useCallback((section, field, value) => {
    setTemplate((prevTemplate) => ({
      ...prevTemplate,
      [section]: {
        ...prevTemplate[section],
        [field]: value,
      },
    }));
  }, []);

  // Handle image upload
  const handleImageUpload = async (section, field, file) => {
    try {
      setImageUploading(true);

      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        // reader.result contains the base64 string
        const base64String = reader.result;
        console.log("Image converted to base64 successfully");

        // Update template with base64 string
        handleTemplateChange(section, field, base64String);

        // Save to database immediately to ensure it's persisted
        handleSaveImage(section, field, base64String);

        setAlert({
          open: true,
          message: "Image uploaded successfully",
          severity: "success",
        });

        setImageUploading(false);
      };

      reader.onerror = () => {
        console.error("Error reading file");
        setAlert({
          open: true,
          message: "Failed to upload image",
          severity: "error",
        });
        setImageUploading(false);
      };

      // Read the file as data URL (base64)
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error uploading image:", error);
      setAlert({
        open: true,
        message: "Failed to upload image",
        severity: "error",
      });
      setImageUploading(false);
    }
  };

  const handleSaveImage = async (section, field, imageData) => {
    try {
      console.log(`Saving ${section}.${field} image to database...`);

      // Make a deep copy of the current template
      const currentTemplate = JSON.parse(JSON.stringify(template));

      // Update the specific image field
      currentTemplate[section][field] = imageData;

      // Save just this update to the database
      const response = await axiosInstance.post("/graphql", {
        query: `
        mutation SaveEventLandingPage($eventId: ID!, $sections: String!, $publicUrl: String!) {
          saveEventLandingPage(eventId: $eventId, sections: $sections, publicUrl: $publicUrl) {
            id
            landingPage {
              publicUrl
            }
          }
        }
      `,
        variables: {
          eventId,
          sections: JSON.stringify(currentTemplate),
          publicUrl: publicUrl || "",
        },
      });

      console.log("Image save response:", response);

      if (response.errors) {
        throw new Error(response.errors[0]?.message || "Error saving image");
      }

      console.log(`${section}.${field} image saved successfully`);
    } catch (error) {
      console.error("Error saving image to database:", error);
      setAlert({
        open: true,
        message:
          "Image uploaded but failed to save to database. Try saving the entire form.",
        severity: "warning",
      });
    }
  };

  // Save landing page
  const handleSave = async () => {
    try {
      setSaving(true);
      console.log("Saving template:", template);
      console.log("Public URL:", publicUrl);

      const response = await axiosInstance.post("/graphql", {
        query: `
          mutation SaveEventLandingPage($eventId: ID!, $sections: String!, $publicUrl: String!) {
            saveEventLandingPage(eventId: $eventId, sections: $sections, publicUrl: $publicUrl) {
              id
              landingPage {
                publicUrl
              }
            }
          }
        `,
        variables: {
          eventId,
          sections: JSON.stringify(template),
          publicUrl: publicUrl || "",
        },
      });

      console.log("Save response:", response);

      if (response.errors) {
        throw new Error(
          response.errors[0]?.message || "Error saving landing page"
        );
      }

      setAlert({
        open: true,
        message: "Landing page saved successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error saving landing page:", error);
      setAlert({
        open: true,
        message: error.message || "Failed to save landing page",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // Color Picker Dialog - Memoized to prevent re-renders
  const ColorPickerDialog = memo(() => {
    const [internalColor, setInternalColor] = useState(tempColor);

    // Update internal color when tempColor changes externally
    useEffect(() => {
      setInternalColor(tempColor);
    }, [tempColor]);

    const handleColorChange = useCallback((color) => {
      setInternalColor(color.hex);
    }, []);

    const handleInputChange = useCallback((event) => {
      setInternalColor(event.target.value);
    }, []);

    const handleCancel = useCallback(() => {
      if (colorField) {
        setTempColor(template[colorField.section][colorField.field]);
      }
      setColorPickerOpen(false);
    }, [colorField, template]);

    const handleApply = useCallback(() => {
      if (colorField) {
        setTempColor(internalColor);
        handleTemplateChange(
          colorField.section,
          colorField.field,
          internalColor
        );
      }
      setColorPickerOpen(false);
    }, [colorField, internalColor, handleTemplateChange]);

    const handleClose = useCallback(() => {
      setColorPickerOpen(false);
    }, []);

    return (
      <Dialog
        open={colorPickerOpen}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        // Prevent dialog from re-rendering on every color change
        disableScrollLock
      >
        <DialogTitle>
          Choose Color
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {colorField && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <SketchPicker
                color={internalColor}
                onChange={handleColorChange}
                disableAlpha
                width="240px"
              />
              <Box sx={{ mt: 2, display: "flex", alignItems: "center" }}>
                <TextField
                  value={internalColor}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "4px",
                            bgcolor: internalColor,
                            border: "1px solid #ddd",
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleApply} color="primary" variant="contained">
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    );
  });

  // Set display name for debugging
  ColorPickerDialog.displayName = "ColorPickerDialog";

  // Handle preview
  const openPreview = () => {
    if (previewUrl) {
      window.open(previewUrl, "_blank");
    }
  };

  // Handle back button
  const handleBack = () => {
    navigate(`/events/view-event/${eventId}`);
  };

  // Open color picker - Memoized to prevent re-renders
  const openColorPicker = useCallback(
    (section, field) => {
      setColorField({ section, field });
      setTempColor(template[section][field]); // Initialize with current color
      setColorPickerOpen(true);
    },
    [template]
  );

  // Render editor sections
  const renderEditor = () => {
    switch (currentSection) {
      case "header":
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Header Settings
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Event Title"
                    fullWidth
                    value={template.header.title}
                    onChange={(e) =>
                      handleTemplateChange("header", "title", e.target.value)
                    }
                    placeholder={eventDetails?.name || "Your Event Name"}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Subtitle"
                    fullWidth
                    value={template.header.subtitle}
                    onChange={(e) =>
                      handleTemplateChange("header", "subtitle", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Header Background
                    </Typography>
                    {template.header.backgroundImage ? (
                      <Box sx={{ position: "relative", mb: 1 }}>
                        <img
                          src={template.header.backgroundImage}
                          alt="Header Background"
                          style={{
                            width: "100%",
                            height: 150,
                            objectFit: "cover",
                            borderRadius: 4,
                          }}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            bgcolor: "rgba(0,0,0,0.5)",
                            color: "white",
                          }}
                          onClick={() =>
                            handleTemplateChange(
                              "header",
                              "backgroundImage",
                              ""
                            )
                          }
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<ImageIcon />}
                          sx={{ mr: 2 }}
                          disabled={imageUploading}
                        >
                          {imageUploading ? "Uploading..." : "Upload Image"}
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                handleImageUpload(
                                  "header",
                                  "backgroundImage",
                                  e.target.files[0]
                                );
                              }
                            }}
                          />
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          or set a background color
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        openColorPicker("header", "backgroundColor")
                      }
                      sx={{ mr: 2, minWidth: 120 }}
                      startIcon={
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "4px",
                            bgcolor: template.header.backgroundColor,
                            border: "1px solid #ddd",
                          }}
                        />
                      }
                    >
                      Background
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      {template.header.backgroundColor}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={() => openColorPicker("header", "textColor")}
                      sx={{ mr: 2, minWidth: 120 }}
                      startIcon={
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "4px",
                            bgcolor: template.header.textColor,
                            border: "1px solid #ddd",
                          }}
                        />
                      }
                    >
                      Text Color
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      {template.header.textColor}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Registration Button
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={template.header.showButton}
                        onChange={(e) =>
                          handleTemplateChange(
                            "header",
                            "showButton",
                            e.target.checked
                          )
                        }
                      />
                    }
                    label="Show Registration Button"
                  />
                </Grid>

                {template.header.showButton && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Button Text"
                        fullWidth
                        value={template.header.buttonText}
                        onChange={(e) =>
                          handleTemplateChange(
                            "header",
                            "buttonText",
                            e.target.value
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Button
                          variant="outlined"
                          onClick={() =>
                            openColorPicker("header", "buttonColor")
                          }
                          sx={{ mr: 2, minWidth: 120 }}
                          startIcon={
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "4px",
                                bgcolor: template.header.buttonColor,
                                border: "1px solid #ddd",
                              }}
                            />
                          }
                        >
                          Button Color
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          {template.header.buttonColor}
                        </Typography>
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        );

      case "about":
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                About Section Settings
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Section Title"
                    fullWidth
                    value={template.about.title}
                    onChange={(e) =>
                      handleTemplateChange("about", "title", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Event Description"
                    fullWidth
                    multiline
                    rows={6}
                    value={template.about.description}
                    onChange={(e) =>
                      handleTemplateChange(
                        "about",
                        "description",
                        e.target.value
                      )
                    }
                    placeholder={
                      eventDetails?.about || "Describe your event here..."
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        openColorPicker("about", "backgroundColor")
                      }
                      sx={{ mr: 2, minWidth: 120 }}
                      startIcon={
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "4px",
                            bgcolor: template.about.backgroundColor,
                            border: "1px solid #ddd",
                          }}
                        />
                      }
                    >
                      Background
                    </Button>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {template.about.backgroundColor}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={() => openColorPicker("about", "titleColor")}
                      sx={{ mr: 2, minWidth: 120 }}
                      startIcon={
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "4px",
                            bgcolor: template.about.titleColor,
                            border: "1px solid #ddd",
                          }}
                        />
                      }
                    >
                      Title Color
                    </Button>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {template.about.titleColor}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={() => openColorPicker("about", "textColor")}
                      sx={{ mr: 2, minWidth: 120 }}
                      startIcon={
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "4px",
                            bgcolor: template.about.textColor,
                            border: "1px solid #ddd",
                          }}
                        />
                      }
                    >
                      Text Color
                    </Button>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {template.about.textColor}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );

      case "speakers":
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Speakers Section Settings
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Section Title"
                    fullWidth
                    value={template.speakers.title}
                    onChange={(e) =>
                      handleTemplateChange("speakers", "title", e.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Section Subtitle"
                    fullWidth
                    value={template.speakers.subtitle}
                    onChange={(e) =>
                      handleTemplateChange(
                        "speakers",
                        "subtitle",
                        e.target.value
                      )
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        openColorPicker("speakers", "backgroundColor")
                      }
                      sx={{ mr: 2, minWidth: 120 }}
                      startIcon={
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "4px",
                            bgcolor: template.speakers.backgroundColor,
                            border: "1px solid #ddd",
                          }}
                        />
                      }
                    >
                      Background
                    </Button>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {template.speakers.backgroundColor}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={() => openColorPicker("speakers", "titleColor")}
                      sx={{ mr: 2, minWidth: 120 }}
                      startIcon={
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "4px",
                            bgcolor: template.speakers.titleColor,
                            border: "1px solid #ddd",
                          }}
                        />
                      }
                    >
                      Title Color
                    </Button>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {template.speakers.titleColor}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={() => openColorPicker("speakers", "textColor")}
                      sx={{ mr: 2, minWidth: 120 }}
                      startIcon={
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "4px",
                            bgcolor: template.speakers.textColor,
                            border: "1px solid #ddd",
                          }}
                        />
                      }
                    >
                      Text Color
                    </Button>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {template.speakers.textColor}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle2">Speakers</Typography>
                    <Chip
                      icon={<InfoIcon />}
                      label="Speakers are managed in the People section"
                      variant="outlined"
                      size="small"
                    />
                  </Box>

                  <Box
                    sx={{
                      maxHeight: 300,
                      overflow: "auto",
                      p: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    {template.speakers.items &&
                    template.speakers.items.length > 0 ? (
                      template.speakers.items.map((speaker, index) => (
                        <Box
                          key={speaker.id || index}
                          sx={{
                            mb: 1,
                            p: 1,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="subtitle2">
                            {speaker.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {speaker.title}
                            {speaker.organization
                              ? ` at ${speaker.organization}`
                              : ""}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                        sx={{ py: 2 }}
                      >
                        No speakers found. Add speakers in the People section.
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );

      case "agenda":
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Agenda Section Settings
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Section Title"
                    fullWidth
                    value={template.agenda.title}
                    onChange={(e) =>
                      handleTemplateChange("agenda", "title", e.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        openColorPicker("agenda", "backgroundColor")
                      }
                      sx={{ mr: 2, minWidth: 120 }}
                      startIcon={
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "4px",
                            bgcolor: template.agenda.backgroundColor,
                            border: "1px solid #ddd",
                          }}
                        />
                      }
                    >
                      Background
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      {template.agenda.backgroundColor}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={() => openColorPicker("agenda", "titleColor")}
                      sx={{ mr: 2, minWidth: 120 }}
                      startIcon={
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "4px",
                            bgcolor: template.agenda.titleColor,
                            border: "1px solid #ddd",
                          }}
                        />
                      }
                    >
                      Title Color
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      {template.agenda.titleColor}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={() => openColorPicker("agenda", "textColor")}
                      sx={{ mr: 2, minWidth: 120 }}
                      startIcon={
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "4px",
                            bgcolor: template.agenda.textColor,
                            border: "1px solid #ddd",
                          }}
                        />
                      }
                    >
                      Text Color
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      {template.agenda.textColor}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={() => openColorPicker("agenda", "accentColor")}
                      sx={{ mr: 2, minWidth: 120 }}
                      startIcon={
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "4px",
                            bgcolor: template.agenda.accentColor,
                            border: "1px solid #ddd",
                          }}
                        />
                      }
                    >
                      Accent Color
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      {template.agenda.accentColor}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle2">Sessions</Typography>
                    <Chip
                      icon={<InfoIcon />}
                      label="Sessions are managed in the Sessions section"
                      variant="outlined"
                      size="small"
                    />
                  </Box>

                  <Box
                    sx={{
                      maxHeight: 300,
                      overflow: "auto",
                      p: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    {template.agenda.items &&
                    template.agenda.items.length > 0 ? (
                      template.agenda.items.map((session, index) => (
                        <Box
                          key={session.id || index}
                          sx={{
                            mb: 1,
                            p: 1,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="subtitle2">
                            {session.title}
                          </Typography>
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            {session.time} ({session.duration})
                          </Typography>
                          <Typography variant="body2">
                            {session.description}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                        sx={{ py: 2 }}
                      >
                        No sessions found. Add sessions in the Sessions section.
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );

      case "sponsors":
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sponsors Section Settings
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Section Title"
                    fullWidth
                    value={template.sponsors.title}
                    onChange={(e) =>
                      handleTemplateChange("sponsors", "title", e.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        openColorPicker("sponsors", "backgroundColor")
                      }
                      sx={{ mr: 2, minWidth: 120 }}
                      startIcon={
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "4px",
                            bgcolor: template.sponsors.backgroundColor,
                            border: "1px solid #ddd",
                          }}
                        />
                      }
                    >
                      Background
                    </Button>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {template.sponsors.backgroundColor}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={() => openColorPicker("sponsors", "titleColor")}
                      sx={{ mr: 2, minWidth: 120 }}
                      startIcon={
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "4px",
                            bgcolor: template.sponsors.titleColor,
                            border: "1px solid #ddd",
                          }}
                        />
                      }
                    >
                      Title Color
                    </Button>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {template.sponsors.titleColor}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={() => openColorPicker("sponsors", "textColor")}
                      sx={{ mr: 2, minWidth: 120 }}
                      startIcon={
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "4px",
                            bgcolor: template.sponsors.textColor,
                            border: "1px solid #ddd",
                          }}
                        />
                      }
                    >
                      Text Color
                    </Button>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {template.sponsors.textColor}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle2">Sponsors</Typography>
                    <Chip
                      icon={<InfoIcon />}
                      label="Sponsors are managed in the Sponsors section"
                      variant="outlined"
                      size="small"
                    />
                  </Box>

                  <Box
                    sx={{
                      maxHeight: 300,
                      overflow: "auto",
                      p: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    {template.sponsors.items &&
                    template.sponsors.items.length > 0 ? (
                      <Grid container spacing={2}>
                        {template.sponsors.items.map((sponsor, index) => (
                          <Grid
                            item
                            xs={6}
                            sm={4}
                            md={3}
                            key={sponsor.id || index}
                          >
                            <Paper
                              sx={{ p: 2, textAlign: "center", height: "100%" }}
                            >
                              {sponsor.logo ? (
                                <Box
                                  component="img"
                                  src={sponsor.logo}
                                  alt={sponsor.name}
                                  sx={{
                                    height: 40,
                                    maxWidth: "100%",
                                    objectFit: "contain",
                                    mb: 1,
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    height: 40,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mb: 1,
                                  }}
                                >
                                  <PhotoIcon color="disabled" />
                                </Box>
                              )}
                              <Typography variant="body2">
                                {sponsor.name}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                        sx={{ py: 2 }}
                      >
                        No sponsors found. Add sponsors in the Sponsors section.
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );

      case "footer":
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Footer Settings
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Footer Text"
                    fullWidth
                    value={template.footer.text}
                    onChange={(e) =>
                      handleTemplateChange("footer", "text", e.target.value)
                    }
                    placeholder={`© ${new Date().getFullYear()} ${
                      eventDetails?.organizedBy || "Event Organizer"
                    }. All rights reserved.`}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        openColorPicker("footer", "backgroundColor")
                      }
                      sx={{ mr: 2, minWidth: 120 }}
                      startIcon={
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "4px",
                            bgcolor: template.footer.backgroundColor,
                            border: "1px solid #ddd",
                          }}
                        />
                      }
                    >
                      Background
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      {template.footer.backgroundColor}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={() => openColorPicker("footer", "textColor")}
                      sx={{ mr: 2, minWidth: 120 }}
                      startIcon={
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "4px",
                            bgcolor: template.footer.textColor,
                            border: "1px solid #ddd",
                          }}
                        />
                      }
                    >
                      Text Color
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      {template.footer.textColor}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  // Initialize data on component mount
  useEffect(() => {
    fetchEventData();
  }, [fetchEventData]);

  // Render loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // The main component render
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Top AppBar */}
      <AppBar
        position="static"
        color="default"
        elevation={0}
        sx={{ borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Toolbar>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleBack}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div">
              Landing Page Builder
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Box>
            <Button
              color="primary"
              startIcon={<VisibilityIcon />}
              onClick={openPreview}
              sx={{ mr: 2 }}
            >
              Preview
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content Area */}
      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
        {/* Left Panel - Section Navigation */}
        <Box
          sx={{
            width: 280,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            borderRight: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Edit Landing Page
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Customize your event landing page by selecting sections below
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1, overflow: "auto" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                p: 2,
                gap: 1,
              }}
            >
              <Button
                variant={currentSection === "header" ? "contained" : "outlined"}
                color={currentSection === "header" ? "primary" : "inherit"}
                sx={{ justifyContent: "flex-start", py: 1.5 }}
                startIcon={<ViewQuiltIcon />}
                onClick={() => setCurrentSection("header")}
              >
                Header
              </Button>

              <Button
                variant={currentSection === "about" ? "contained" : "outlined"}
                color={currentSection === "about" ? "primary" : "inherit"}
                sx={{ justifyContent: "flex-start", py: 1.5 }}
                startIcon={<TextFieldsIcon />}
                onClick={() => setCurrentSection("about")}
              >
                About
              </Button>

              <Button
                variant={
                  currentSection === "speakers" ? "contained" : "outlined"
                }
                color={currentSection === "speakers" ? "primary" : "inherit"}
                sx={{ justifyContent: "flex-start", py: 1.5 }}
                startIcon={<PeopleIcon />}
                onClick={() => setCurrentSection("speakers")}
              >
                Speakers
              </Button>

              <Button
                variant={currentSection === "agenda" ? "contained" : "outlined"}
                color={currentSection === "agenda" ? "primary" : "inherit"}
                sx={{ justifyContent: "flex-start", py: 1.5 }}
                startIcon={<EventNoteIcon />}
                onClick={() => setCurrentSection("agenda")}
              >
                Agenda
              </Button>

              <Button
                variant={
                  currentSection === "sponsors" ? "contained" : "outlined"
                }
                color={currentSection === "sponsors" ? "primary" : "inherit"}
                sx={{ justifyContent: "flex-start", py: 1.5 }}
                startIcon={<BusinessIcon />}
                onClick={() => setCurrentSection("sponsors")}
              >
                Sponsors
              </Button>

              <Button
                variant={currentSection === "footer" ? "contained" : "outlined"}
                color={currentSection === "footer" ? "primary" : "inherit"}
                sx={{ justifyContent: "flex-start", py: 1.5 }}
                startIcon={<ContentCopyIcon />}
                onClick={() => setCurrentSection("footer")}
              >
                Footer
              </Button>
            </Box>
          </Box>

          {/* Public URL Display */}
          {publicUrl && (
            <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
              <Typography variant="subtitle2" gutterBottom>
                Public URL
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ wordBreak: "break-all" }}
              >
                {`${window.location.origin}/events/preview/${publicUrl}`}
              </Typography>
              <Button
                variant="text"
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={() => {
                  try {
                    // Try the modern navigator.clipboard API first
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                      navigator.clipboard
                        .writeText(
                          `${window.location.origin}/events/preview/${publicUrl}`
                        )
                        .then(() => {
                          setAlert({
                            open: true,
                            message: "URL copied to clipboard",
                            severity: "success",
                          });
                        })
                        .catch((err) => {
                          console.error("Clipboard writeText error:", err);
                          // If clipboard API fails, use fallback
                          copyToClipboardFallback();
                        });
                    } else {
                      // If clipboard API is not available, use fallback
                      copyToClipboardFallback();
                    }
                  } catch (error) {
                    console.error("Copy URL error:", error);
                    setAlert({
                      open: true,
                      message:
                        "Failed to copy URL. Please select and copy manually.",
                      severity: "error",
                    });
                  }
                }}
                sx={{ mt: 1 }}
              >
                Copy URL
              </Button>
            </Box>
          )}
        </Box>

        {/* Right Panel - Split between editor and preview */}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Box sx={{ display: "flex", height: "100%" }}>
            {/* Editor Panel */}
            <Box
              sx={{
                width: "40%",
                p: 3,
                overflow: "auto",
                borderRight: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="h6" gutterBottom>
                {currentSection.charAt(0).toUpperCase() +
                  currentSection.slice(1)}{" "}
                Section
              </Typography>
              {renderEditor()}
            </Box>

            {/* Preview Panel */}
            <Box sx={{ width: "60%", overflow: "auto" }}>
              <LandingPagePreview
                template={template}
                eventDetails={eventDetails}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Color Picker Dialog */}
      <ColorPickerDialog />

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          elevation={6}
          variant="filled"
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LandingPageBuilder;
